// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
  takeUntil,
} from 'rxjs';
import { GetAllFeatureDetailsForSelectedYearInReleaseNotes$Params } from 'src/app/api/remote-api/.portal.generated/fn/feature-details/get-all-feature-details-for-selected-year-in-release-notes';
import { GetYearImages$Params } from 'src/app/api/remote-api/.portal.generated/fn/feature-details/get-year-images';
import {
  ReleaseNotesModel,
  VersionInfoModel,
  YearImages,
} from 'src/app/api/remote-api/.portal.generated/models';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { DeepReadonly } from 'src/app/modules/shared/utils/api-call-state.util';
import { FeatureDetailsService } from '../../services/feature-details.service';
import { FormatService } from '../../services/format.service';
import { ReleaseNotesService } from '../../services/release-notes.service';
import { SearchService } from '../../services/search.service';

export class MergedFeatureDetails {
  public featureFolderName: string;
  public featureTitle: string;
  public featureDetails: string;
  public images: string[];

  constructor(
    featureFolderName: string,
    featureTitle: string,
    featureDetails: string,
    images: string[]
  ) {
    this.featureFolderName = featureFolderName;
    this.featureTitle = featureTitle;
    this.featureDetails = featureDetails;
    this.images = images;
  }
}

export class MergedVersionDetails {
  public releaseVersion: string;
  public releaseDate: string;
  public versionNumber: number;
  public features: MergedFeatureDetails[];

  constructor(
    releaseVersion: string,
    releaseDate: string,
    versionNumber: number,
    features: MergedFeatureDetails[]
  ) {
    this.releaseVersion = releaseVersion;
    this.releaseDate = releaseDate;
    this.features = features;
    this.versionNumber = versionNumber;
  }
}

export class MergedYearDetails {
  public releaseYear: string;
  public versions: MergedVersionDetails[];

  constructor(releaseYear: string, versions: MergedVersionDetails[]) {
    this.releaseYear = releaseYear;
    this.versions = versions;
  }
}

@Component({
  selector: 'app-release-notes',
  standalone: true,
  imports: [CommonModule, TranslateModule, LocalizedDatePipe],
  templateUrl: './release-notes.component.html',
  styleUrls: ['./release-notes.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReleaseNotesComponent implements OnInit, OnDestroy {
  @Input() isReleaseNotesPageVisible = false;
  @Output() isReleaseNotesPageVisibleChange = new EventEmitter<boolean>();

  yearList$ = this.releaseNotesService.releaseYears.lastSuccessfulData$;

  yearImages$ = this.releaseNotesService.yearImages.lastSuccessfulData$;

  featureList$ = this.releaseNotesService.featureListForSelectedYear;
  featureDetails$ = this.featureList$.lastSuccessfulData$;

  filteredVersionList$: Observable<DeepReadonly<Array<VersionInfoModel>>> =
    new ReplaySubject(); // To store filtered versions

  mergedYearDetails!: MergedYearDetails;

  selectedVersion = '';
  selectedYear = '';
  searchTerm = '';

  private readonly destroy$ = new Subject<void>();

  matchCount$ = this.searchService.matchCount$;
  currentMatch$ = this.searchService.currentMatch$;

  /**
   *
   */
  constructor(
    protected readonly featureDetailsService: FeatureDetailsService,
    protected readonly releaseNotesService: ReleaseNotesService,
    public screenLayoutService: ScreenLayoutService,
    protected readonly formatService: FormatService,
    protected readonly searchService: SearchService
  ) {}

  ngOnInit(): void {
    this.getYears();
    this.featureDetailsService.getStoredSesoData().subscribe((sesoData) => {
      this.selectedYear = sesoData.SesoReleasedYear;
      this.selectedVersion = sesoData.SesoReleasedVersion;
      this.onYearChange(this.selectedYear);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getYears(): void {
    this.releaseNotesService.releaseYears.request();
  }

  onYearChange(selectedYear: string): void {
    this.selectedYear = selectedYear;
    this.getReleaseNotesForSelectedYear(
      { year: this.selectedYear },
      { year: this.selectedYear }
    );
  }

  getReleaseNotesForSelectedYear(
    getAllFeatureDetailsForSelectedYear$Params: GetAllFeatureDetailsForSelectedYearInReleaseNotes$Params,
    getYearImages$Params: GetYearImages$Params
  ): void {
    this.releaseNotesService.yearImages.request(getYearImages$Params);
    this.releaseNotesService.featureListForSelectedYear.request(
      getAllFeatureDetailsForSelectedYear$Params
    );

    combineLatest([this.yearImages$, this.featureDetails$])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([yearImages, featureList]) => {
          this.mergedYearDetails =
            this.CreateMergedFeatureDetailsForSelectedYear(
              yearImages,
              featureList
            );
        },
      });
  }

  private CreateMergedFeatureDetailsForSelectedYear(
    yearImages: DeepReadonly<YearImages> | null,
    featureList: DeepReadonly<Array<ReleaseNotesModel>> | null
  ): MergedYearDetails {
    if (!this.isValidInput(yearImages, featureList)) {
      return new MergedYearDetails(this.selectedYear, []);
    }

    const mergedVersions = this.attachImagesToFeatures(
      yearImages as DeepReadonly<YearImages>,
      featureList as DeepReadonly<Array<ReleaseNotesModel>>
    );

    mergedVersions.sort((a, b) => b.versionNumber - a.versionNumber);

    return new MergedYearDetails(this.selectedYear, mergedVersions);
  }

  private isValidInput(
    yearImages: DeepReadonly<YearImages> | null,
    featureList: DeepReadonly<Array<ReleaseNotesModel>> | null
  ): boolean {
    if (yearImages?.versions && featureList) {
      return featureList.length > 0;
    }
    return false;
  }

  private attachImagesToFeatures(
    yearImages: DeepReadonly<YearImages>,
    featureListForYear: DeepReadonly<Array<ReleaseNotesModel>>
  ): Array<MergedVersionDetails> {
    const mergedVersionDetails: Array<MergedVersionDetails> = [];
    if (!featureListForYear && !featureListForYear[0]) {
      return mergedVersionDetails;
    }

    const versionMap = new Map<string, MergedVersionDetails>(); // (releaseVersion, MergedVersionDetails)

    for (const feature of featureListForYear) {
      //the 3 consts created here are for a specific feature.
      const releaseVersion = feature.releaseVersion ?? 'Unknown Version';
      const releaseDate = this.ensureISOFormat(
        feature.releaseDate ?? 'Unknown Date'
      );
      const releaseNumber = Number(feature.releaseNumber ?? 0);

      let versionEntry = versionMap.get(releaseVersion);
      if (!versionEntry) {
        versionEntry = new MergedVersionDetails(
          releaseVersion,
          releaseDate,
          releaseNumber,
          []
        );
        versionMap.set(releaseVersion, versionEntry);
      }

      // Find matching images for the feature
      const versionImages = yearImages.versions?.find(
        (v) => v.versionName === releaseVersion
      );
      const featureImages = versionImages?.features?.find(
        (f) => f.featureFolderName === feature.featureFolderName
      );

      const mergedFeature = new MergedFeatureDetails(
        feature.featureFolderName ?? '',
        feature.featureTitle ?? '',
        feature.featureDetails ?? '',
        [...(featureImages?.images?.pcImages ?? [])]
      );
      // Add the feature to the version's features list
      versionEntry.features.push(mergedFeature);
    }
    mergedVersionDetails.push(...Array.from(versionMap.values()));

    return mergedVersionDetails;
  }

  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.searchService.search(searchTerm);
  }

  onNextMatch(): void {
    this.searchService.navigateNext();
  }

  onPreviousMatch(): void {
    this.searchService.navigatePrevious();
  }

  formatFeatureDetails(details: string): string {
    return this.formatService.formatFeatureDescription(details);
  }

  private ensureISOFormat(date: string): string {
    if (!isNaN(Date.parse(date))) {
      return new Date(date).toISOString();
    }
    return date;
  }
}
